import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, Plane, UtensilsCrossed, MapPin, ChevronLeft, Search, LogOut, Bell, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import MobileNav from "@/components/MobileNav";
import AirportSwitcher from "@/components/AirportSwitcher";
import { useSavedAirports } from "@/hooks/useSavedAirports";
import { SavedAirportsList } from "@/components/airport/SavedAirportsList";
import { SaveAirportButton } from "@/components/airport/SaveAirportButton";
import { NotificationSettings } from "@/components/NotificationSettings";

interface Flight {
  id?: string;
  flight_number: string;
  airline: string;
  destination: string;
  destination_code: string;
  gate: string | null;
  departure_time: string;
  status: string;
  status_color: string;
}

interface Restaurant {
  id: number;
  name: string;
  category: string;
  terminal: string;
  hours: string;
  location: string;
  rating?: number;
  userRatingsTotal?: number;
  url: string;
  placeId: string;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { signOut } = useAuth();
  const { savedAirports, isSaved, toggleSave } = useSavedAirports();
  const initialState = location.state || { airport: "Unknown", terminal: "A", flightNumber: undefined, defaultTab: "flights" };
  
  const [currentAirport, setCurrentAirport] = useState(initialState.airport);
  const [currentTerminal, setCurrentTerminal] = useState(initialState.terminal);
  const [flightNumber] = useState(initialState.flightNumber);
  const [activeTab, setActiveTab] = useState<string>(initialState.defaultTab || "flights");
  const [mobileActiveTab, setMobileActiveTab] = useState<"home" | "flights" | "dining" | "wait-times">("wait-times");
  
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [flights, setFlights] = useState<Flight[]>([]);
  const [loadingFlights, setLoadingFlights] = useState(true);
  const [standardWait, setStandardWait] = useState({ time: 0, status: "default" as "default" | "secondary" | "destructive" });
  const [precheckWait, setPrecheckWait] = useState<{ time: number; status: "default" | "secondary" | "destructive"; available: boolean } | null>(null);
  const [loadingTsa, setLoadingTsa] = useState(true);
  const [flightSearch, setFlightSearch] = useState("");
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);

  const handleSignOut = async () => {
    await signOut();
    toast({
      title: "Signed out",
      description: "You've been successfully signed out.",
    });
    navigate("/auth");
  };

  const handleAirportChange = (airport: string, terminal: string) => {
    setCurrentAirport(airport);
    setCurrentTerminal(terminal);
  };

  const handleMobileTabChange = (tab: "flights" | "dining") => {
    setActiveTab(tab === "flights" ? "flights" : "restaurants");
    setMobileActiveTab(tab);
  };

  // Fetch real TSA wait times
  useEffect(() => {
    const fetchTsaWaitTimes = async () => {
      try {
        setLoadingTsa(true);
        const { data, error } = await supabase.functions.invoke('get-tsa-wait-times', {
          body: { airport: currentAirport }
        });

        if (error) throw error;

        if (data) {
          // Handle standard security
          if (data.standard) {
            setStandardWait({
              time: data.standard.waitTime,
              status: data.standard.status === 'Low' ? 'default' : 
                     data.standard.status === 'Medium' ? 'secondary' : 
                     'destructive'
            });
          }

          // Handle TSA PreCheck
          if (data.precheck?.available) {
            setPrecheckWait({
              time: data.precheck.waitTime,
              status: data.precheck.status === 'Low' ? 'default' : 
                     data.precheck.status === 'Medium' ? 'secondary' : 
                     'destructive',
              available: true
            });
          } else {
            setPrecheckWait({ time: 0, status: 'default', available: false });
          }
        }
      } catch (error) {
        console.error('Error fetching TSA wait times:', error);
        // Use default values on error
        setStandardWait({ time: 15, status: 'default' });
        setPrecheckWait({ time: 0, status: 'default', available: false });
      } finally {
        setLoadingTsa(false);
      }
    };

    if (currentAirport) {
      fetchTsaWaitTimes();
    }
  }, [currentAirport]);

  // Fetch real flight data
  useEffect(() => {
    const fetchFlights = async () => {
      try {
        setLoadingFlights(true);
        const { data, error } = await supabase.functions.invoke('get-flights', {
          body: { airport: currentAirport, terminal: currentTerminal }
        });

        if (error) throw error;

        if (data?.flights) {
          setFlights(data.flights);
        }
      } catch (error) {
        console.error('Error fetching flights:', error);
        toast({
          title: "Error loading flights",
          description: "Unable to load flight data. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoadingFlights(false);
      }
    };

    if (currentAirport && currentTerminal) {
      fetchFlights();
    }
  }, [currentAirport, currentTerminal, toast]);

  // Fetch real restaurant data
  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        setLoadingRestaurants(true);
        const { data, error } = await supabase.functions.invoke('get-restaurants', {
          body: { airport: currentAirport, terminal: currentTerminal }
        });

        if (error) throw error;

        if (data?.restaurants) {
          setRestaurants(data.restaurants);
        }
      } catch (error) {
        console.error('Error fetching restaurants:', error);
        toast({
          title: "Error loading restaurants",
          description: "Unable to load restaurant data. Showing limited options.",
          variant: "destructive",
        });
      } finally {
        setLoadingRestaurants(false);
      }
    };

    if (currentAirport && currentTerminal) {
      fetchRestaurants();
    }
  }, [currentAirport, currentTerminal, toast]);

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: true 
    });
  };


  const filteredFlights = flightSearch.trim() 
    ? flights.filter(flight => 
        flight.flight_number.toLowerCase().includes(flightSearch.toLowerCase()) ||
        flight.destination.toLowerCase().includes(flightSearch.toLowerCase()) ||
        flight.destination_code.toLowerCase().includes(flightSearch.toLowerCase()) ||
        flight.airline?.toLowerCase().includes(flightSearch.toLowerCase())
      )
    : flights;

  const filteredRestaurants = selectedCategory === "all" 
    ? restaurants 
    : restaurants.filter(r => r.category === selectedCategory);

  const categories = ["all", ...Array.from(new Set(restaurants.map(r => r.category)))];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/30 pb-20 md:pb-0">
      <div className="container max-w-2xl mx-auto px-4 py-4 md:py-6 space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/home")}
              className="h-11 w-11 min-h-[44px] min-w-[44px] hidden md:flex"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            
            <AirportSwitcher
              currentAirport={currentAirport}
              currentTerminal={currentTerminal}
              onAirportChange={handleAirportChange}
            />

            <SaveAirportButton
              airportCode={currentAirport}
              isSaved={isSaved(currentAirport)}
              onToggle={toggleSave}
            />
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            className="h-11 min-h-[44px] gap-2 hidden md:flex"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>
        
        {flightNumber && (
          <p className="text-sm text-muted-foreground px-1">
            Tracking Flight {flightNumber}
          </p>
        )}

        {/* Saved Airports Quick Access */}
        <SavedAirportsList
          savedAirports={savedAirports}
          onSelect={handleAirportChange}
        />

        {/* Saved Restaurants link */}
        <Link
          to="/saved-restaurants"
          className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-card hover:border-primary/30 transition-colors"
        >
          <span className="inline-flex items-center gap-2 text-sm font-medium">
            <Heart className="h-4 w-4 text-red-500 fill-red-500" aria-hidden="true" />
            Your saved restaurants
          </span>
          <span className="text-xs text-muted-foreground">View all →</span>
        </Link>

        {/* TSA Wait Time Cards */}
        <div className="grid gap-3 md:gap-4 grid-cols-2">
          {/* Standard Security */}
          <Card className="p-4 md:p-6 shadow-md border-border/50 transition-all duration-200">
            {loadingTsa ? (
              <div className="animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 rounded-xl bg-muted skeleton-shimmer" />
                  <div className="space-y-2">
                    <div className="h-3 w-16 rounded bg-muted skeleton-shimmer" />
                    <div className="h-8 w-12 rounded bg-muted skeleton-shimmer" />
                  </div>
                </div>
                <div className="h-3 w-24 rounded bg-muted skeleton-shimmer" />
              </div>
            ) : (
              <>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 md:p-3 bg-primary/10 rounded-xl flex-shrink-0">
                      <Clock className="h-5 w-5 md:h-6 md:w-6 text-primary" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">Standard</p>
                      <p className="text-2xl md:text-3xl font-bold text-foreground" aria-label={`${standardWait.time} minute wait time`}>
                        {standardWait.time > 0 ? `${standardWait.time}m` : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <Badge variant={standardWait.status} className="self-start md:mt-1">
                    {standardWait.status === "default" ? "Low" : standardWait.status === "secondary" ? "Medium" : "High"}
                  </Badge>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mt-2 md:mt-4">Real-time TSA data</p>
              </>
            )}
          </Card>

          {/* TSA PreCheck */}
          <Card className="p-4 md:p-6 shadow-md border-border/50 transition-all duration-200">
            {loadingTsa ? (
              <div className="animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 rounded-xl bg-muted skeleton-shimmer" />
                  <div className="space-y-2">
                    <div className="h-3 w-16 rounded bg-muted skeleton-shimmer" />
                    <div className="h-8 w-12 rounded bg-muted skeleton-shimmer" />
                  </div>
                </div>
                <div className="h-3 w-24 rounded bg-muted skeleton-shimmer" />
              </div>
            ) : precheckWait?.available ? (
              <>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 md:p-3 bg-success/10 rounded-xl flex-shrink-0">
                      <Clock className="h-5 w-5 md:h-6 md:w-6 text-success" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">PreCheck ✓</p>
                      <p className="text-2xl md:text-3xl font-bold text-foreground" aria-label={`${precheckWait.time} minute PreCheck wait time`}>
                        {precheckWait.time}m
                      </p>
                    </div>
                  </div>
                  <Badge variant={precheckWait.status} className="self-start md:mt-1">
                    {precheckWait.status === "default" ? "Low" : precheckWait.status === "secondary" ? "Medium" : "High"}
                  </Badge>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mt-2 md:mt-4">Expedited screening</p>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <div className="p-2.5 md:p-3 bg-muted rounded-xl flex-shrink-0">
                  <Clock className="h-5 w-5 md:h-6 md:w-6 text-muted-foreground" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-medium">PreCheck</p>
                  <p className="text-sm text-muted-foreground mt-1">Not available</p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Tabs for Flights and Restaurants */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-card shadow-md h-12 rounded-xl">
            <TabsTrigger value="flights" className="gap-2 min-h-[44px] text-sm rounded-lg transition-all duration-200">
              <Plane className="h-4 w-4" aria-hidden="true" />
              Flights
            </TabsTrigger>
            <TabsTrigger value="restaurants" className="gap-2 min-h-[44px] text-sm rounded-lg transition-all duration-200">
              <UtensilsCrossed className="h-4 w-4" aria-hidden="true" />
              Dining
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2 min-h-[44px] text-sm rounded-lg transition-all duration-200">
              <Bell className="h-4 w-4" aria-hidden="true" />
              Alerts
            </TabsTrigger>
          </TabsList>

          {/* Flights Tab */}
          <TabsContent value="flights" className="space-y-3 mt-3 md:mt-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Input
                type="text"
                placeholder="Search flights by number, destination, or airline..."
                value={flightSearch}
                onChange={(e) => setFlightSearch(e.target.value)}
                className="pl-11"
                aria-label="Search flights"
              />
            </div>

            {loadingFlights ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="p-4 shadow-md animate-pulse">
                    <div className="flex items-start justify-between mb-3">
                      <div className="space-y-2">
                        <div className="h-5 w-20 rounded bg-muted skeleton-shimmer" />
                        <div className="h-4 w-32 rounded bg-muted skeleton-shimmer" />
                      </div>
                      <div className="h-6 w-16 rounded-full bg-muted skeleton-shimmer" />
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="h-4 w-16 rounded bg-muted skeleton-shimmer" />
                      <div className="h-4 w-14 rounded bg-muted skeleton-shimmer" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : filteredFlights.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-base">
                  {flightSearch ? "No flights match your search." : "No flights available for this terminal."}
                </p>
              </div>
            ) : (
              filteredFlights.map((flight, index) => (
                <Card key={flight.id || index} className="p-4 shadow-[var(--shadow-card)] border-border/50 hover:shadow-[var(--shadow-elevated)] transition-shadow active:scale-[0.98] touch-manipulation">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="font-semibold text-foreground text-base">{flight.flight_number}</p>
                      <p className="text-sm text-muted-foreground">
                        {flight.destination} {flight.destination_code && `(${flight.destination_code})`}
                      </p>
                    </div>
                    <Badge 
                      variant={
                        flight.status_color === "success" ? "default" : 
                        flight.status_color === "warning" ? "secondary" : 
                        "outline"
                      }
                      className={
                        flight.status_color === "success" ? "bg-success text-success-foreground" :
                        flight.status_color === "warning" ? "bg-warning text-warning-foreground" :
                        "bg-primary text-primary-foreground"
                      }
                    >
                      {flight.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                    {flight.gate && (
                      <span className="flex items-center gap-1.5 min-h-[24px]">
                        <MapPin className="h-4 w-4" />
                        Gate {flight.gate}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5 min-h-[24px]">
                      <Clock className="h-4 w-4" />
                      {formatTime(flight.departure_time)}
                    </span>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Restaurants Tab */}
          <TabsContent value="restaurants" className="space-y-3 md:space-y-4 mt-3 md:mt-4">
            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1" role="tablist" aria-label="Restaurant categories">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className="capitalize whitespace-nowrap min-h-[44px] px-4 touch-manipulation transition-all duration-200"
                  role="tab"
                  aria-selected={selectedCategory === category}
                >
                  {category}
                </Button>
              ))}
            </div>

            {/* Restaurant List */}
            <div className="space-y-3">
              {loadingRestaurants ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="p-4 shadow-md animate-pulse">
                      <div className="flex items-start justify-between mb-2">
                        <div className="space-y-2 flex-1">
                          <div className="h-5 w-32 rounded bg-muted skeleton-shimmer" />
                          <div className="h-4 w-20 rounded bg-muted skeleton-shimmer" />
                        </div>
                        <div className="h-6 w-20 rounded-full bg-muted skeleton-shimmer" />
                      </div>
                      <div className="flex items-center gap-4 mt-3">
                        <div className="h-4 w-24 rounded bg-muted skeleton-shimmer" />
                        <div className="h-4 w-20 rounded bg-muted skeleton-shimmer" />
                      </div>
                    </Card>
                  ))}
                </div>
              ) : filteredRestaurants.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-base">No dining options found for this terminal.</p>
                </div>
              ) : (
                filteredRestaurants.map((restaurant) => (
                  <Card key={restaurant.id} className="p-4 shadow-[var(--shadow-card)] border-border/50 hover:shadow-[var(--shadow-elevated)] transition-shadow active:scale-[0.98] touch-manipulation">
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <div className="flex-1 min-w-0">
                        <a 
                          href={restaurant.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-semibold text-foreground hover:text-primary transition-colors underline text-base min-h-[44px] inline-flex items-center"
                        >
                          {restaurant.name}
                        </a>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <p className="text-sm text-muted-foreground">{restaurant.category}</p>
                          {restaurant.rating && (
                            <span className="text-xs text-muted-foreground">
                              ⭐ {restaurant.rating} ({restaurant.userRatingsTotal})
                            </span>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline" className="border-primary/30 text-primary flex-shrink-0">
                        T{restaurant.terminal}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1.5 min-h-[24px]">
                        <Clock className="h-3.5 w-3.5" />
                        {restaurant.hours}
                      </span>
                      <span className="flex items-center gap-1.5 min-h-[24px]">
                        <MapPin className="h-3.5 w-3.5" />
                        {restaurant.location}
                      </span>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="mt-4">
            <NotificationSettings />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileNav 
        activeTab={mobileActiveTab} 
        onTabChange={handleMobileTabChange}
      />
    </div>
  );
};

export default Dashboard;
